import { useRef, useState } from 'react';
import { getUploadFunction, type SignResponse } from '@/services/uploads';
import { useAnalytics } from '@/services/analytics';
import { checkFile, type AcceptedExtension } from './file-rules';
import type { ImportSource, SheetSource } from './types';

type UseSourceUploadOptions = {
  onChange: (source: ImportSource | null) => void;
  onUploadingChange: ((isUploading: boolean) => void) | undefined;
  signUpload: (file: { size: number; name: string; contentType: string }) => Promise<SignResponse>;
  acceptedExtensions: readonly AcceptedExtension[];
  maxFileSizeBytes: number | undefined;
  onSourceDiscarded: ((change: { reason: 'deleted' | 'replaced'; kind: ImportSource['kind'] }) => void) | undefined;
};

/**
 * The source field's lifecycle: which upload is the current one, whether it is still running, and
 * what went wrong.
 *
 * Separate from the field it serves because the ordering it keeps is the hard part, and it has
 * nothing to do with what the field draws: a source can be replaced or thrown away while an
 * upload for the previous one is still in the air, and the answer that arrives last must not be
 * the one that wins.
 */
export function useSourceUpload({
  onChange,
  onUploadingChange,
  signUpload,
  acceptedExtensions,
  maxFileSizeBytes,
  onSourceDiscarded,
}: UseSourceUploadOptions) {
  const analytics = useAnalytics();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateIsUploading = (next: boolean) => {
    setIsUploading(next);
    onUploadingChange?.(next);
  };
  // Bumped whenever the source changes, so an upload that resolves after the
  // user picked a sheet (or cleared the field) can't overwrite the newer choice.
  const sourceId = useRef(0);

  // Superseding an upload transfers ownership of the uploading flag: the old
  // upload's `finally` no longer clears it, so whatever superseded it must.
  const supersedeSource = () => {
    sourceId.current += 1;
    updateIsUploading(false);
    setError(null);
  };

  const selectSheet = (sheet: SheetSource) => {
    supersedeSource();
    onChange({ kind: 'sheet', sheet });
  };

  const reportDeleting = (kind: ImportSource['kind']) => {
    onSourceDiscarded?.({ reason: 'deleted', kind });
  };

  const clearSource = (kind: ImportSource['kind']) => {
    reportDeleting(kind);
    supersedeSource();
    onChange(null);
  };

  const reportReplacing = (kind: ImportSource['kind']) => {
    onSourceDiscarded?.({ reason: 'replaced', kind });
  };

  const selectFile = async (file: File, viaDragAndDrop: boolean) => {
    const check = checkFile(file, acceptedExtensions, maxFileSizeBytes);
    if (!check.ok) {
      setError(check.error);
      return;
    }
    // Tracked only once the file clears validation, so rejected picks don't inflate the
    // upload count against the uploads that actually start.
    void analytics.track('import', 'uploadStarted', { source: 'from_disk', dragAndDrop: viaDragAndDrop });
    const currentSourceId = sourceId.current + 1;
    sourceId.current = currentSourceId;
    setError(null);
    updateIsUploading(true);
    try {
      const signature = await signUpload({ size: file.size, name: file.name, contentType: check.contentType });
      const { uploadFn, data } = getUploadFunction(signature);
      const { key } = await uploadFn(file, data, {});
      if (sourceId.current !== currentSourceId) return;

      onChange({ kind: 's3', s3Import: key, filename: file.name });
    } catch {
      if (sourceId.current !== currentSourceId) return;

      setError("File couldn't be uploaded. Try again.");
    } finally {
      // A superseded upload must not report "done" — a newer upload may still be
      // running, and clearing the flag here would unlock submit against the older
      // source. Whoever superseded this one already cleared it.
      if (sourceId.current === currentSourceId) updateIsUploading(false);
    }
  };

  return { isUploading, error, selectFile, selectSheet, clearSource, reportDeleting, reportReplacing };
}
